// Practicums page content. Hard rules: the internal codename never appears
// anywhere (copy, code, comments, URLs, assets); no em-dashes; honest scope
// stays visible; no traction or validation claims the product cannot back.

// ---------------------------------------------------------------------------
// VIDEOS: three films are prepared on YouTube. Paste each video's embed URL
// (https://www.youtube.com/embed/VIDEO_ID) into `embed` below and it renders
// as a player. Leave null to show the designed placeholder.
// ---------------------------------------------------------------------------
export const videos = {
  marketing: {
    embed: null, // e.g. 'https://www.youtube.com/embed/XXXXXXXXXXX'
    title: 'Practicums in ninety seconds',
    caption: 'The exam room, simulated: what a program gets.',
  },
  fullCase: {
    embed: null,
    title: 'Watch a full encounter',
    caption: 'A complete station, start to finish: briefing, conversation, examination, note, and scorecard.',
  },
  caseBuilding: {
    embed: null,
    title: 'Building a case',
    caption: 'How faculty author a station: identity, clinical content, findings, personality, and the publishing gate.',
  },
};

// The scripted mini-encounter for the interactive specimen. A miniature of the
// real disclosure-gate mechanic: general questions get general answers; the
// specific question opens the gate. Patient and presentation follow the
// documentation's own worked example.
export const miniEncounter = {
  patient: 'Margaret Chen, 74',
  doorInstruction:
    'Margaret Chen is a 74-year-old woman with three weeks of worsening breathlessness. Take a focused history. You have three questions.',
  turns: [
    {
      options: [
        { id: 'a', text: 'How are you sleeping?', reply: 'Not great, doctor.', kind: 'general' },
        {
          id: 'b',
          text: 'How many pillows do you sleep on, and has that changed?',
          reply: 'Three now. It used to be one. If I lie flat I cannot breathe.',
          kind: 'gate',
          gate: 'orthopnea',
        },
        { id: 'c', text: 'Is there any family history of cancer?', reply: 'No, nothing like that.', kind: 'off' },
      ],
    },
    {
      options: [
        { id: 'a', text: 'Any other symptoms?', reply: 'Not really.', kind: 'general' },
        {
          id: 'b',
          text: 'Have you noticed swelling in your ankles, or your weight going up?',
          reply: 'My shoes stopped fitting last week. I am up maybe four pounds.',
          kind: 'gate',
          gate: 'swelling',
        },
        { id: 'c', text: 'Do you smoke?', reply: 'Never have.', kind: 'off' },
      ],
    },
    {
      options: [
        {
          id: 'a',
          text: 'Are you taking any medications?',
          reply: 'The water pill, and something for my blood pressure.',
          kind: 'general',
        },
        {
          id: 'b',
          text: 'Are you taking every medicine exactly as prescribed, every day?',
          reply: 'Well. I stopped the water pill two weeks ago. It had me up all night.',
          kind: 'gate',
          gate: 'adherence',
        },
        { id: 'c', text: 'Do you have any allergies?', reply: 'Penicillin.', kind: 'off' },
      ],
    },
  ],
  // Key features scored by the mini evaluator, using the product's real logic
  // vocabulary: explicit logic, weights, and the hurdle rule.
  keyFeatures: [
    {
      id: 'kf1',
      label: 'Elicits the discriminating congestion symptoms by name (all of: orthopnea; ankle swelling or weight gain)',
      logic: (gates) => gates.includes('orthopnea') && gates.includes('swelling'),
      flag: 'essential',
      points: 2,
    },
    {
      id: 'kf2',
      label: 'Directly establishes medication adherence (all of: adherence asked as its own question)',
      logic: (gates) => gates.includes('adherence'),
      flag: 'safety-critical',
      points: 3,
    },
  ],
};

export const twoIntelligences = [
  {
    name: 'The Patient AI',
    text: 'Briefed on one person: her history, her symptoms, her personality, her worries. It converses in real time, in her voice, at her level of health literacy, and it never volunteers what sits behind a disclosure gate. Ask a general question, get a general answer. Ask the discriminating question, get the fact.',
  },
  {
    name: 'The Evaluator AI',
    text: 'A separate intelligence that reads the full encounter afterward and scores it against the station\u2019s explicit key features: what was asked, what was examined, what was reasoned, what was missed. The actor never grades the candidate; the roles are separated in software the way an OSCE separates them in the room.',
  },
  {
    name: 'The instructor, in charge',
    text: 'A three-panel review workspace: the transcript, a per-domain scorecard with instructor override on every score, and a chat where the instructor can question the evaluator about its own reasoning. Override is a permanent part of the design, not a temporary concession.',
  },
];

export const encounterAnatomy = [
  {
    name: 'The door instruction',
    text: 'Who the patient is, what you are asked to do, and any constraint on how. Reading time is yours; nothing runs until you press Begin, exactly as on the real exam.',
  },
  {
    name: 'The room',
    text: 'The patient on video, a live transcript filling in as you both speak, an elapsed timer, a mic toggle, and a red End session button. Your camera matters: conduct is observed, and the patient reacts to it.',
  },
  {
    name: 'The conversation',
    text: 'Just talk. The patient answers what you ask, at their level of health literacy, and never volunteers what sits behind a gate. Their affect responds to your conduct turn by turn, and some changes, once triggered, do not go back.',
  },
  {
    name: 'The examination',
    text: 'You examine by saying what you are doing, out loud. When the patient recognizes the request, an overlay presents the finding: a video clip with synced auscultation, an image, or a report. Your mic is muted to the patient while it is up, so you can think aloud.',
  },
  {
    name: 'Investigations',
    text: 'Same mechanism: ask for the ECG, the bloods, the film, and read the result yourself, laid out like a real report. Every request is recorded before the finding returns: there is no way to look at a result invisibly, so ask for what you have a reason to ask for.',
  },
  {
    name: 'The note, then the scorecard',
    text: 'Stations with an oral-question equivalent ask for a written note: diagnosis and management plan, required, before any feedback. Then the evaluator reads everything and the scorecard is built.',
  },
];

export const scoringPoints = [
  {
    title: 'Key features, with explicit logic',
    text: 'A station is scored against the specific things a competent candidate must do on this presentation, each with explicit logic: all of, at least N of, any of. Partial credit inside a key feature does not exist; two of three named investigations is not two thirds, it is not satisfied.',
  },
  {
    title: 'Hurdles before numbers',
    text: 'Safety-critical features weigh 3, essential features 2, everything else 1. Missing any essential feature fails the station\u2019s hurdle regardless of the percentage, and the report says so first: hurdles, then key features, then the number last. Showing a comfortable percentage above a missed safety item would teach the wrong lesson.',
  },
  {
    title: 'Abstention, never a guess',
    text: 'Where there is genuinely no evidence either way, the evaluator abstains, and abstained items are dropped from both sides of the fraction rather than counted as zero. An abstained item is never a miss against the candidate. Attempts below the engagement floor are stated plainly as too short to judge.',
  },
  {
    title: 'Formative first',
    text: 'By default a student sees coaching without the number: strengths, gaps, and the behavioural markers, which are observations rather than marks. The full scorecard is released when the instructor releases it.',
  },
];

export const caseAuthoring = [
  {
    name: 'One document, whole station',
    text: 'A case is a single structured document: patient identity and demographics, avatar and voice, chief complaint and door instruction, the full history, social and family context, personality and behaviour, physical findings, and the key features it will be scored against.',
  },
  {
    name: 'Findings as media',
    text: 'Physical examinations and investigations are authored as the material the student will actually see: examination video clips with synced auscultation, images, laboratory panels laid out like real reports.',
  },
  {
    name: 'Blueprint-mapped',
    text: 'Cases carry blueprint metadata, so a station is a curriculum object: what it teaches, what it assesses, and where it sits in a program\u2019s map.',
  },
  {
    name: 'A deliberate publishing gate',
    text: 'Cases move draft to published through a one-time gate. Clinical content authored with AI assistance waits for physician certification before students see it. That gate exists deliberately and is not shortcut.',
  },
];

export const honestScope = [
  {
    title: 'Validation is the standard, and the study is not done',
    text: 'The evidence that the evaluator\u2019s scoring agrees with human examiners is the standard this product is built to meet, and the validation study has not been run yet. We say that plainly rather than implying otherwise.',
  },
  {
    title: 'The instructor holds the pen',
    text: 'Every score carries per-domain instructor override, and the default release model is formative coaching first, numbers when the instructor releases them. The human stays in charge of consequence.',
  },
  {
    title: 'Certified before a student sees it',
    text: 'AI-assisted clinical content passes physician certification before it is served. The same liability rule that governs the rest of EdAI governs the simulation lab.',
  },
  {
    title: 'What it replaces, and what it does not',
    text: 'Practicums gives programs OSCE-style assessment capacity without standardized-patient scheduling and cost, for practice and formative evaluation. It is not a regulator, not an accreditor, and not an endorsement; those words belong to the authorities.',
  },
];

export const faq = [
  {
    q: 'Is the patient scripted?',
    a: 'The patient is briefed, not scripted. It converses freely in character, answers what you ask at the patient\u2019s level of health literacy, and holds gated facts back until the discriminating question is asked, because that is how real patients behave and exactly what the OSCE tests.',
  },
  {
    q: 'What stops the patient from grading me leniently?',
    a: 'The patient cannot grade you at all. Scoring is done by a separate Evaluator AI that reads the completed encounter against the station\u2019s explicit key features. The separation of the actor from the examiner is the architecture, not a setting.',
  },
  {
    q: 'Can the evaluator be wrong?',
    a: 'It can, which is why it abstains rather than guesses when evidence is missing, why every score carries instructor override, and why the instructor can interrogate the evaluator about its own reasoning in the review workspace. Validation against human examiners is the standard the product is built to meet; that study has not been run yet, and we say so.',
  },
  {
    q: 'How do encounters map to our curriculum?',
    a: 'Cases carry blueprint metadata and integrate natively with EdAI MedSchool, so encounters, their scorecards, and the competency evidence they generate land in the same institutional record as the rest of a student\u2019s work.',
  },
  {
    q: 'What does it cost a program compared to standardized patients?',
    a: 'The honest answer is that it depends on your volume and your current standardized-patient program, so we would rather model it with you than publish a number. Ask us and bring your numbers.',
  },
];
